import React, { useState, useMemo, useEffect } from 'react';
import { Compass, Sparkles, Check, X, HelpCircle, Activity, FolderOpen, List, ArrowRight, Save, Printer, Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle, ShieldCheck, Target, Loader2, Brain } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import EditableAIBox from './EditableAIBox';
import DOMPurify from 'dompurify';


const CAREER_SCENARIOS = [
  { id: 1, group: 'A', text: "Organik ve sürdürülebilir tarım teknolojileri tasarlamak." },
  { id: 2, group: 'B', text: "Akıllı evler ve çevre dostu modern yapılar tasarlamak." },
  { id: 3, group: 'C', text: "Büyük bir ekibi yöneterek kriz anlarında şirket kurtaran stratejik kararlar almak." },
  { id: 4, group: 'D', text: "Milyonların izleyeceği bir dijital içerik, film veya animasyon tasarlamak." },
  { id: 5, group: 'E', text: "İnsanların psikolojik sorunlarına çözüm bulup onlara zor anlarında yol göstermek." },
  { id: 6, group: 'F', text: "İnsan davranışlarının ve toplumsal olayların arkasında yatan gizli nedenleri araştırmak." },
  { id: 7, group: 'G', text: "Sık sık yurt dışına seyahat ederek farklı ülkelerden insanlarla diplomatik görüşmeler yapmak." },
  { id: 8, group: 'H', text: "İnsan beyninin işleyişini inceleyen ileri düzey tıbbi araştırmalar yapmak." },
  { id: 9, group: 'I', text: "Borsada, kripto paralarda veya küresel piyasalarda finansal analizler yapmak." },
  { id: 10, group: 'İ', text: "İnsan hayatını kolaylaştıracak yeni bir yapay zeka algoritması kodlamak." },
  { id: 11, group: 'A', text: "Nesli tükenmekte olan canlıları koruma projelerinde sahada görev almak." },
  { id: 12, group: 'B', text: "Üç boyutlu (3D) yazıcılarla yeni, işlevsel ve estetik endüstriyel ürünler üretmek." },
  { id: 13, group: 'C', text: "Haksızlığa uğrayan kişilerin haklarını mahkeme salonlarında savunmak." },
  { id: 14, group: 'D', text: "Kendi müzik grubunu veya tasarım markanı kurarak kitlelere hitap etmek." },
  { id: 15, group: 'E', text: "Gençlere veya çocuklara yeni bilgiler öğreterek onların hayatlarına dokunmak." },
  { id: 16, group: 'F', text: "Tarihteki büyük olayları inceleyerek bugünün dünya sorunlarına ışık tutmak." },
  { id: 17, group: 'G', text: "Yabancı dilleri akıcı bir şekilde konuşarak uluslararası krizleri çözmek veya çeviriler yapmak." },
  { id: 18, group: 'H', text: "Ameliyathanede veya acil serviste insanların hayatını doğrudan kurtarmak." },
  { id: 19, group: 'I', text: "Bir şirketin gelir-gider dengesini yöneterek onu kârlı bir yapıya dönüştürmek." },
  { id: 20, group: 'İ', text: "Gelişmiş siber güvenlik ağları kurarak sistemleri hackerlara karşı korumak." },
  { id: 21, group: 'A', text: "Küresel iklim krizine karşı çevreci ve yenilenebilir enerji çözümleri geliştirmek." },
  { id: 22, group: 'B', text: "Otonom (sürücüsüz) araçların mekanik tasarımlarını ve aerodinamiğini planlamak." },
  { id: 23, group: 'C', text: "Uluslararası bir markanın yöneticisi (CEO) olarak markayı küresel pazara taşımak." },
  { id: 24, group: 'D', text: "Estetik ve görsel zevki yüksek dijital grafikler, illüstrasyonlar üretmek." },
  { id: 25, group: 'E', text: "Etkili konuşma yeteneğiyle kitleleri ikna eden bir iletişim veya reklam uzmanı olmak." },
  { id: 26, group: 'F', text: "Farklı kültürlerin yaşam tarzlarını, inançlarını ve felsefelerini derinlemesine incelemek." },
  { id: 27, group: 'G', text: "Dünyanın farklı bölgelerindeki turizm ve kültür elçisi olarak kıtalar arası çalışmak." },
  { id: 28, group: 'H', text: "Yeni nesil ilaçlar veya genetik tedaviler geliştiren bir laboratuvarda çalışmak." },
  { id: 29, group: 'I', text: "Ekonomik verileri ve istatistikleri inceleyerek gelecekteki krizleri önceden tahmin etmek." },
  { id: 30, group: 'İ', text: "Geleceğin teknolojisi olan robotik sistemleri ve otonom yazılımları geliştirmek." }
];

const CAREER_GROUPS = {
  'A': { name: "Doğa ve Tarım", color: "#10b981", desc: "Ziraat Müh., Orman Müh., Çevre Müh., Su Ürünleri, Maden Müh., Meteoroloji, Jeoloji, Coğrafya, Arkeoloji, Veterinerlik vb." },
  'B': { name: "Mekanik ve Teknoloji", color: "#f59e0b", desc: "Mimarlık, İç Mimarlık, Makine Müh., Tekstil Müh., Radyo-TV Tek., Teknik Ressam, Diş Teknisyenliği vb." },
  'C': { name: "Liderlik ve Yönetim", color: "#3b82f6", desc: "Hukuk (Avukat, Hakim, Savcı), Siyasal Bilimler, İşletme, Yönetim Bilişim Sistemleri, Yöneticilik vb." },
  'D': { name: "Sanat ve Medya", color: "#ec4899", desc: "Fotoğraf, Sinema, Tiyatro, Müzik, Resim, Heykeltıraşlık, Spor, Peyzaj Mimarlığı, Dekoratörlük, Sanat Tarihi vb." },
  'E': { name: "Edebiyat ve Sözel İletişim", color: "#8b5cf6", desc: "Edebiyat, Gazetecilik, İletişim, Öğretmenlik, Reklamcılık, Halkla İlişkiler, PDR, İlahiyat vb." },
  'F': { name: "Sosyal Bilimler", color: "#14b8a6", desc: "Psikoloji, Sosyoloji, Felsefe, Antropoloji, Hukuk, Kamu Yönetimi, Gazetecilik, Halkla İlişkiler vb." },
  'G': { name: "Uluslararası İlişkiler", color: "#06b6d4", desc: "Uluslararası İlişkiler, Mütercim-Tercümanlık, Filoloji, Turizm-Otelcilik vb." },
  'H': { name: "Tıp ve Sağlık", color: "#ef4444", desc: "Tıp, Diş Hekimliği, Eczacılık, Hemşirelik, Biyoloji, Kimya, Veterinerlik, Moleküler Biyoloji vb." },
  'I': { name: "Finans ve Analiz", color: "#6366f1", desc: "İktisat, Ekonomi, Maliye, Bankacılık, Kamu ve Özel Sektör Yöneticiliği, Muhasebe vb." },
  'İ': { name: "Yazılım ve Bilişim", color: "#84cc16", desc: "Bilgisayar Müh., Yazılım Müh., Elektrik-Elektronik, İnşaat, Uçak Müh., Endüstri Müh., Matematik, Fizik vb." }
};

const LGS_GROUPS = {
  'Fen Lisesi': { name: 'Fen Lisesi', color: '#10b981', desc: 'Sayısal, analitik ve bilimsel odaklı akademik lise.' },
  'Anadolu Lisesi': { name: 'Anadolu Lisesi', color: '#3b82f6', desc: 'Dengeli, genel akademik yetkinlik ve çok yönlülük.' },
  'Sosyal Bilimler Lisesi': { name: 'Sosyal Bilimler Lisesi', color: '#8b5cf6', desc: 'Sözel, edebiyat, tarih ve toplum bilimleri odaklı.' },
  'Mesleki ve Teknik Anadolu Lisesi': { name: 'Mesleki ve Teknik Anadolu Lisesi', color: '#f59e0b', desc: 'Pratik, atölye, teknoloji ve meslek odaklı.' },
  'Güzel Sanatlar ve Spor Lisesi': { name: 'Güzel Sanatlar ve Spor Lisesi', color: '#ec4899', desc: 'Sanatsal, fiziksel yetenek ve estetik odaklı.' }
};

const SCORE_TYPES = ['TYT', 'SAY', 'EA', 'SÖZ', 'DİL'];
const LGS_SCORE_TYPES = ['GENEL', 'İL', 'OBP']; // OBP Eklendi

// GÜNCELLENDİ: Artık submissions (tüm veriler) ve onSaveSubmission (kaydetme fonksiyonu) içeri alındı.
const CareerCompass = ({ user, submissions = [], onSaveSubmission, onAnalyze, onUpgrade }) => {
  // YENİ: Öğrenci Seçim ve Çapraz Veri State'leri
  const [selectedStudentSub, setSelectedStudentSub] = useState(null);
  const [studentCrossData, setStudentCrossData] = useState({}); // Holland, Çoklu Zeka vb. geçmiş veriler burada toplanacak.

  // Kariyer Pusulasını VEYA LGS Pusulasını çözmüş olan öğrencileri filtrele
  const compassSubmissions = submissions.filter(s => 
    s.testId === 'kariyer_pusulasi' || 
    s.testId === 'lgs_pusulasi' || 
    s.testName?.includes('Kariyer Pusulası') || 
    s.testName?.includes('LGS')
  );

  const handleSelectStudent = (subId) => {
    if (!subId) {
       setSelectedStudentSub(null);
       setStudentCrossData({});
       return;
    }

const mainSub = compassSubmissions.find(s => String(s.firebaseId || s.id) === String(subId));
    if (!mainSub) return;
    
    setSelectedStudentSub(mainSub);
    
    // SİHİRLİ DOKUNUŞ: Bu öğrencinin geçmişte çözdüğü diğer envanterleri bul (Holland, Zeka vb.)
    const otherTests = submissions.filter(s => s.studentNo === mainSub.studentNo && (s.firebaseId || s.id) !== mainSub.firebaseId);
    const crossData = {};
    otherTests.forEach(t => {
        if (t.testId.includes('holland')) crossData.holland = t.reportData?.summary || "Holland verisi var";
        if (t.testId.includes('coklu_zeka')) crossData.cokluZeka = t.reportData?.topIntelligences?.map(x=>x.subject).join(', ') || "Zeka verisi var";
        if (t.testId.includes('degerler')) crossData.degerler = t.reportData?.topValues?.map(x=>x.subject).join(', ') || "Değer verisi var";
       // YENİ: 160 Soruluk Eğilim Testi Verisi (Barajsız En Yüksek 3 Alan ve Puanları)
        if (t.testId === 'mesleki_egilim_belirleme') {
           const topScores = [...(t.reportData?.detailedScores || [])].sort((a,b) => b.score - a.score).slice(0, 3);
           const topEgilim = topScores.map(x => `${CAREER_GROUPS[x.groupKey]?.name} (${x.score} Puan)`).join(', ');
           if(topEgilim) crossData.meslekiEgilim = topEgilim;
        }
    });
    setStudentCrossData(crossData);

    // Öğrencinin test cevaplarını (veya bitmiş analizi) state'e yükle
    if (mainSub.reportData && mainSub.reportData.scores) {
       // Test artık cep telefonundan çözüldüğü ve motordan geçtiği için hazır puanları alıyoruz
       setScores(mainSub.reportData.scores);
       setIsTestFinished(true); 
    }
// 👇 GÜNCELLENEN KISIM: HEM FIREBASE HEM DE GEÇİCİ HAFIZA (F5 KORUMASI) 👇
    const draftId = `career_draft_${mainSub.firebaseId || mainSub.id}`;
    const savedDraft = sessionStorage.getItem(draftId);

    if (savedDraft) {
       // 1. ÖNCELİK: Sayfa yenilenmişse veya sekme değişmişse son yazılanları tarayıcıdan kurtar!
       const parsed = JSON.parse(savedDraft);
       setStudentRanks(parsed.studentRanks || { TYT: '', SAY: '', EA: '', SÖZ: '', DİL: '' });
       setPreferences(parsed.preferences || Array.from({ length: 24 }, (_, i) => ({ id: i, university: '', department: '', scoreType: 'SAY', rank: '' })));
       setAiResult(mainSub.careerSessionData?.aiResult || null);
    } 
    else if (mainSub.careerSessionData) {
       // 2. ÖNCELİK: Tarayıcıda veri yoksa, Firebase'deki son "Manuel Kaydedilmiş" veriyi getir
       setStudentRanks(mainSub.careerSessionData.studentRanks || { TYT: '', SAY: '', EA: '', SÖZ: '', DİL: '' });
       setPreferences(mainSub.careerSessionData.preferences || Array.from({ length: 24 }, (_, i) => ({ id: i, university: '', department: '', scoreType: 'SAY', rank: '' })));
       setAiResult(mainSub.careerSessionData.aiResult || null);
    } else {
       // 3. ÖNCELİK: Hiç kayıt yoksa ekranı tertemiz sıfırla
       setStudentRanks({ TYT: '', SAY: '', EA: '', SÖZ: '', DİL: '' });
       setPreferences(Array.from({ length: 24 }, (_, i) => ({ id: i, university: '', department: '', scoreType: 'SAY', rank: '' })));
       setAiResult(null);
    }
  };


  const [activeTab, setActiveTab] = useState('compass');

  // SEKME 1 STATE
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [scores, setScores] = useState({ A:0, B:0, C:0, D:0, E:0, F:0, G:0, H:0, I:0, 'İ':0 });
  const [isTestFinished, setIsTestFinished] = useState(false);
  const [startManual, setStartManual] = useState(false);

 // SEKME 2 STATE: YKS/LGS Çift Motorlu Excel Mantığı
  const [examType, setExamType] = useState('YKS'); // Varsayılan sınav türü YKS
  const [studentRanks, setStudentRanks] = useState({ TYT: '', SAY: '', EA: '', SÖZ: '', DİL: '', GENEL: '', İL: '', OBP: '' });
  const [preferences, setPreferences] = useState(
    Array.from({ length: 24 }, (_, i) => ({ id: i, university: '', department: '', scoreType: 'SAY', rank: '' }))
  );

  // SİHİRLİ DOKUNUŞ: Sınav türü değiştiğinde tabloyu ve verileri temizleyip o moda hazırlar.
  const handleExamTypeSwitch = (type) => {
    if (type === examType) return;
    if (window.confirm(`Sınav türünü ${type} olarak değiştirmek mevcut tercih listenizi sıfırlayacaktır. Onaylıyor musunuz?`)) {
        setExamType(type);
        setStudentRanks({ TYT: '', SAY: '', EA: '', SÖZ: '', DİL: '', GENEL: '', İL: '', OBP: '' });
        // LGS seçilirse 20 satır açılır: İlk 5'i OBP, kalanı GENEL türünde gelir.
        setPreferences(Array.from({ length: type === 'YKS' ? 24 : 20 }, (_, i) => ({ 
            id: Date.now() + i, 
            university: '', 
            department: '', 
            scoreType: type === 'YKS' ? 'SAY' : (i < 5 ? 'OBP' : 'GENEL'), 
            rank: '' 
        })));
        setAiResult(null);
    }
  };

  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [showBoarding, setShowBoarding] = useState(false); // YENİ: LGS Pansiyonlu Okullar Tablosu

  // SEKME 3 STATE
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [localTrialCount, setLocalTrialCount] = useState(user?.aiTrialCount || 0);
  const [uiModal, setUiModal] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });

// 🛡️ SİHİRLİ DOKUNUŞ 1: OTOMATİK KAYIT (DEBOUNCE + SESSION STORAGE)
  // Kullanıcı klavyeden elini çektikten 500ms sonra veriyi sessizce tarayıcıya yedekler.
  useEffect(() => {
    if (!selectedStudentSub) return;
    const draftId = `career_draft_${selectedStudentSub.firebaseId || selectedStudentSub.id}`;
    
    const timeoutId = setTimeout(() => {
      const draftData = { studentRanks, preferences };
      sessionStorage.setItem(draftId, JSON.stringify(draftData));
    }, 500); // 500 milisaniye gecikme (Kasmayı önler)

    return () => clearTimeout(timeoutId);
  }, [studentRanks, preferences, selectedStudentSub]);

// 👇 YENİ EKLENECEK KISIM: KAYDETME MOTORU 👇
  const handleSaveSession = async () => {
    if (!selectedStudentSub) return alert("Lütfen önce çalışmayı kaydedeceğiniz öğrenciyi seçin.");
    
    // O anki tüm çalışmayı paketliyoruz
    const updatedSub = {
       ...selectedStudentSub,
       careerSessionData: {
          studentRanks,
          preferences,
          aiResult,
          lastUpdated: new Date().toLocaleString('tr-TR') // Tarih/Saat damgası
       }
    };

   try {
       await onSaveSubmission(updatedSub);
       
       // 🧹 KAYIT BAŞARILIYSA TARAYICI YEDEĞİNİ TEMİZLE
       sessionStorage.removeItem(`career_draft_${selectedStudentSub.firebaseId || selectedStudentSub.id}`);
       
       alert(`✅ Çalışmanız başarıyla kaydedildi! (Son Kayıt: ${updatedSub.careerSessionData.lastUpdated})\n\nÖğrenciyi listeden tekrar seçtiğinizde kaldığınız yerden devam edebilirsiniz.`);
    } catch (e) {
       alert("⚠️ Kaydetme sırasında bir hata oluştu.");
    }
  };

  const handleAnswer = (points) => {
    const currentScenario = CAREER_SCENARIOS[currentCardIndex];
    setScores(prev => ({ ...prev, [currentScenario.group]: prev[currentScenario.group] + points }));
    if (currentCardIndex < CAREER_SCENARIOS.length - 1) {
      setCurrentCardIndex(prev => prev + 1);
    } else {
      setIsTestFinished(true);
    }
  };

  
 // YENİ: Performans için useMemo ile belleğe alındı ve Çift Motorlu Zırh Eklendi
  const chartData = useMemo(() => {
    // Seçili testin LGS olup olmadığını anla
    const isLGS = selectedStudentSub?.testId === 'lgs_pusulasi' || selectedStudentSub?.testName?.includes('LGS');
    const currentGroups = isLGS ? LGS_GROUPS : CAREER_GROUPS;

    return Object.keys(scores)
      .filter(key => currentGroups[key]) 
      .map(key => ({
        name: currentGroups[key].name, 
        score: scores[key], 
        color: currentGroups[key].color, 
        desc: currentGroups[key].desc
      })).sort((a, b) => b.score - a.score);
  }, [scores, selectedStudentSub]);
  const topGroups = chartData.slice(0, 3);

  const handleRankChange = (type, value) => setStudentRanks(prev => ({ ...prev, [type]: value }));

  // YENİ: Bölüm kontrolü eklendi
  const getActivePrefs = () => preferences.filter(p => p.university && p.department && p.rank);

 // --- TERCİH MASASI FONKSİYONLARI (EXCEL & AKILLI KUTU) ---
  const handlePrefChange = (index, field, value) => {
    const newPrefs = [...preferences];
    newPrefs[index][field] = value;
    setPreferences(newPrefs);
  };

  const deletePref = (index) => {
    const newPrefs = [...preferences];
    newPrefs.splice(index, 1); 
    newPrefs.push({ id: Date.now() + Math.random(), university: '', department: '', scoreType: 'SAY', rank: '' });
    setPreferences(newPrefs);
  };

  const movePref = (index, direction) => {
    const newPrefs = [...preferences];
    
    // YENİ: YKS ve LGS Tablo Sınırlarını Belirleme
    let minIdx = 0; let maxIdx = 23;
    if (examType === 'LGS') {
        if (index >= 0 && index < 5) { minIdx = 0; maxIdx = 4; } // Yerel
        else if (index >= 5 && index < 15) { minIdx = 5; maxIdx = 14; } // Merkezi
        else if (index >= 15 && index < 20) { minIdx = 15; maxIdx = 19; } // Pansiyonlu
    }

    if (direction === 'up' && index > minIdx) {
      [newPrefs[index - 1], newPrefs[index]] = [newPrefs[index], newPrefs[index - 1]];
      setPreferences(newPrefs);
    } else if (direction === 'down' && index < maxIdx) {
      [newPrefs[index + 1], newPrefs[index]] = [newPrefs[index], newPrefs[index + 1]];
      setPreferences(newPrefs);
    }
  };

  const handleSmartPaste = () => {
    if (!pasteText.trim()) return;
    const lines = pasteText.split('\n').filter(l => l.trim() !== '');
    const newPrefs = [...preferences];
    
    let pIdx = newPrefs.findIndex(p => p.university === '' && p.department === '' && p.rank === '');
    if (pIdx === -1) pIdx = 0;
    
    lines.forEach(line => {
      if (pIdx >= 24) return;
      
      const lowerLine = line.toLowerCase();
      // İŞTE DÜZELTİLEN YER: Sadece "Üniversite" VE "Bölüm" aynı satırdaysa atla (Başlık koruması)
      if (lowerLine.includes('üniversite') && lowerLine.includes('bölüm')) return;

      const typeMatch = line.match(/\b(SAY|EA|SÖZ|DİL|TYT)\b/i);
      let scoreType = typeMatch ? typeMatch[1].toUpperCase() : 'SAY';
      
      const cleanLineForRank = line.replace(/\./g, ''); 
      const numbers = cleanLineForRank.match(/\d{3,}/g); 
      let rank = '';
      if (numbers && numbers.length > 0) rank = numbers[numbers.length - 1];

      let normalizedLine = line.replace(/[|│¦,\-–—]/g, '\t');
      const parts = normalizedLine.split(/\t+|\s{2,}/).map(s => s.trim()).filter(s => s);
      
      const textParts = parts.filter(p => !p.match(/^(SAY|EA|SÖZ|DİL|TYT)$/i) && !p.replace(/\./g, '').match(/^\d+$/));
      
      let university = textParts[0] || '';
      let department = textParts[1] || '';

      if (university || department || rank) {
        newPrefs[pIdx].university = university;
        newPrefs[pIdx].department = department;
        newPrefs[pIdx].scoreType = scoreType;
        newPrefs[pIdx].rank = rank;
        pIdx++;
      }
    });
    
    setPreferences(newPrefs);
    setShowPasteModal(false);
    setPasteText('');
  };

 const getRankBadge = (prefRank, scoreType) => {
    const sRank = parseFloat(studentRanks[scoreType]); 
    if (!sRank) return { label: `${scoreType} Girilmedi`, color: 'bg-rose-100 text-rose-700 border-rose-300', icon: AlertTriangle };
    
    const pRank = parseFloat(prefRank);
    
    // OBP MATEMATİĞİ (Ters Orantı: Yüksek puan daha iyidir)
    if (scoreType === 'OBP') {
        const diff = sRank - pRank; // Öğrenci - Okul (Örn: Öğrenci 85, Okul 90 ise diff = -5)
        if (diff < -5) return { label: 'Çok Riskli', color: 'bg-rose-100 text-rose-800', icon: AlertTriangle };
        if (diff < -2) return { label: 'Sürpriz', color: 'bg-rose-50 text-rose-700', icon: Target };
        if (diff <= 2) return { label: 'Muhtemel', color: 'bg-amber-50 text-amber-700', icon: Target };
        if (diff <= 5) return { label: 'Güvenli Liman', color: 'bg-emerald-50 text-emerald-700', icon: ShieldCheck };
        return { label: 'Tam Garanti', color: 'bg-blue-50 text-blue-700', icon: ShieldCheck };
    }

    // LGS YÜZDELİK DİLİM MATEMATİĞİ (Düşük oran daha iyidir)
    if (examType === 'LGS') {
        if (pRank < sRank * 0.50) return { label: 'Çok Riskli', color: 'bg-rose-100 text-rose-800', icon: AlertTriangle };
        if (pRank < sRank * 0.80) return { label: 'Sürpriz', color: 'bg-rose-50 text-rose-700', icon: Target };
        if (pRank <= sRank * 1.20) return { label: 'Muhtemel', color: 'bg-amber-50 text-amber-700', icon: Target };
        if (pRank <= sRank * 2.00) return { label: 'Güvenli Liman', color: 'bg-emerald-50 text-emerald-700', icon: ShieldCheck };
        return { label: 'Tam Garanti', color: 'bg-blue-50 text-blue-700', icon: ShieldCheck };
    }

    // YKS SIRALAMA MATEMATİĞİ
    const riskLimits = { 'SAY': 0.70, 'SÖZ': 0.65, 'EA': 0.70, 'DİL': 0.75, 'TYT': 0.50 };
    const safeLimits = { 'SAY': 1.70, 'SÖZ': 1.80, 'EA': 1.90, 'DİL': 1.50, 'TYT': 2.50 };

    const minMultiplier = riskLimits[scoreType] || 0.70;
    const maxMultiplier = safeLimits[scoreType] || 1.70;

    if (pRank < sRank * minMultiplier) return { label: 'Çok Riskli', color: 'bg-rose-100 text-rose-800', icon: AlertTriangle };
    if (pRank < sRank * 0.9) return { label: 'Sürpriz', color: 'bg-rose-50 text-rose-700', icon: Target };
    if (pRank <= sRank * 1.1) return { label: 'Muhtemel', color: 'bg-amber-50 text-amber-700', icon: Target };
    if (pRank <= sRank * maxMultiplier) return { label: 'Güvenli Liman', color: 'bg-emerald-50 text-emerald-700', icon: ShieldCheck };
    return { label: 'Tam Garanti', color: 'bg-blue-50 text-blue-700', icon: ShieldCheck };
  };
  


  // 1. AŞAMA: KONTROLLER VE ONAY PENCERESİNİ AÇMA
  const handleGenerateAI = async () => {
    const isPremiumUser = user && (user?.isPremium === true || user?.plan === 'premium' || user?.role === 'admin' || user?.plan === 'pro');
    const trialCount = localTrialCount; 
    const MAX_TRIAL = 2;

    if (!isPremiumUser && trialCount >= MAX_TRIAL) {
       setAiResult(null); 
       setActiveTab('ai'); 
       return; 
    }

    const activePrefs = getActivePrefs();
    if (activePrefs.length === 0) {
        return setUiModal({ isOpen: true, type: 'alert', title: 'Eksik Veri', message: 'Analiz için en az 1 tam tercih (Üniversite ve Sıralama) girmiş olmanız gerekmektedir.' });
    }
    
    const filledRanks = Object.entries(studentRanks).filter(([k,v]) => v).map(([k,v]) => `${k}: ${v}`).join(' | ');
    if (!filledRanks) {
        return setUiModal({ isOpen: true, type: 'alert', title: 'Eksik Veri', message: 'Analiz için öğrencinin en az bir puan türündeki sıralamasını girmelisiniz.' });
    }

    if (!isPremiumUser) {
       setUiModal({
           isOpen: true,
           type: 'confirm',
           title: 'Deneme Hakkı Kullanımı',
           message: `Ücretsiz sürümde ${MAX_TRIAL} deneme hakkınız bulunmaktadır.\nŞu ana kadar ${trialCount} hakkınızı kullandınız. Bu analizi başlatmak istiyor musunuz?`,
           onConfirm: () => executeAIEngine(isPremiumUser, activePrefs, filledRanks)
       });
    } else {
       executeAIEngine(isPremiumUser, activePrefs, filledRanks);
    }
  };

  // 2. AŞAMA: ONAY VERİLDİKTEN SONRA ÇALIŞACAK MOTOR
  const executeAIEngine = async (isPremiumUser, activePrefs, filledRanks) => {
    setUiModal({ isOpen: false }); // Modal'ı kapat
    setAiLoading(true);
    
    try {
      let richProfile = `1. Kariyer Pusulası Yönelimleri: ${isTestFinished ? topGroups.map(g => g.name).join(', ') : "Çözülmedi"}\n`;
      richProfile += `2. Holland Mesleki İlgi Tipi: ${studentCrossData.holland || "Çözülmedi"}\n`;
      richProfile += `3. Çoklu Zeka Baskın Alanları: ${studentCrossData.cokluZeka || "Çözülmedi"}\n`;
      richProfile += `4. Mesleki Değerleri (Beklentileri): ${studentCrossData.degerler || "Çözülmedi"}\n`;
      richProfile += `5. Mesleki Eğilim Testi (160 Soru) Güçlü Alanlar: ${studentCrossData.meslekiEgilim || "Çözülmedi"}\n`;

      const interests = richProfile; 
      
      let prefListText = "";
      if (examType === 'YKS') {
          prefListText = activePrefs.map((p, i) => `${i+1}. Sırada: ${p.university} - ${p.department} (${p.scoreType} Puanı, Sıra: ${p.rank})`).join('\n');
      } else {
          // LGS Tablolarını Ayır ve Kendi İçinde Numaralandır
          const activeLocal = preferences.slice(0, 5).filter(p => p.university && p.rank);
          const activeCentral = preferences.slice(5, 15).filter(p => p.university && p.rank);
          const activeBoarding = showBoarding ? preferences.slice(15, 20).filter(p => p.university && p.rank) : [];
          
          prefListText = `DİKKAT: BU BİR LGS (LİSE GEÇİŞ SİSTEMİ) TERCİHİDİR. LÜTFEN İLK TABLOYU OBP (OKUL BAŞARI PUANI), DİĞER TABLOLARI İSE YÜZDELİK DİLİM (Örn: %4.20) KULLANARAK DEĞERLENDİRİN.\n\n`;
          prefListText += `Öğrencinin OBP Puanı: ${studentRanks.OBP || "Girilmedi"}, Genel Yüzdelik Dilimi: %${studentRanks.GENEL || "Girilmedi"}\n\n`;
          
          prefListText += `[YEREL YERLEŞTİRME TERCİHLERİ - OBP İLE ALAN OKULLAR]\n` + 
              (activeLocal.length > 0 ? activeLocal.map((p, i) => `${i+1}. Okul: ${p.university} - ${p.department} (Okulun Geçen Yılki OBP'si: ${p.rank})`).join('\n') : "Boş") + "\n\n";
              
          prefListText += `[MERKEZÎ YERLEŞTİRME TERCİHLERİ - YÜZDELİK DİLİMLE ALAN OKULLAR]\n` + 
              (activeCentral.length > 0 ? activeCentral.map((p, i) => `${i+1}. Okul: ${p.university} - ${p.department} (Geçen Yıl Dilimi: %${p.rank})`).join('\n') : "Boş") + "\n\n";
              
          if (showBoarding) {
              prefListText += `[PANSİYONLU OKULLAR - YÜZDELİK DİLİMLE ALAN OKULLAR]\n` + 
                  (activeBoarding.length > 0 ? activeBoarding.map((p, i) => `${i+1}. Okul: ${p.university} - ${p.department} (Geçen Yıl Dilimi: %${p.rank})`).join('\n') : "Boş");
          }
      }

      const NEW_API_URL = "https://us-central1-rehber360-e0808.cloudfunctions.net/analyzeCareerCompass";

      const res = await fetch(NEW_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filledRanks: filledRanks,
          interests: interests,
          prefListText: prefListText,
          userId: user?.uid || user?.id,
          examType: examType 
        })
      });

      if (!res.ok) throw new Error("Sunucu hatası veya bağlantı problemi.");
      const data = await res.json();
      
      setAiResult(data.result);
      setActiveTab('ai');
      
      if (!isPremiumUser) setLocalTrialCount(prev => prev + 1);
      
    } catch (error) {
      setUiModal({ isOpen: true, type: 'alert', title: 'Hata', message: error.message });
    } finally {
      setAiLoading(false);
    }
  };

// YENİ: Yapay Zeka Metnini PDF İçin Kusursuzlaştıran ve GÜVENLİ Çevirmen
  const formatAIText = (text) => {
    if(!text) return { __html: '' };
    let formatted = text
        .replace(/# (.*?)\n/g, '<h3 class="text-lg font-black text-blue-900 mt-8 mb-3 uppercase tracking-wide border-b border-blue-100 pb-2">$1</h3>')
        .replace(/## (.*?)\n/g, '<h4 class="text-md font-black text-slate-800 mt-5 mb-2 uppercase">$1</h4>')
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-black text-slate-900">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em class="italic text-slate-700">$1</em>')
        .replace(/\n/g, '<br/>');
    
    // XSS Saldırılarına karşı kalkan: DOMPurify
    return { __html: DOMPurify.sanitize(formatted) };
  };

  // --- RESMİ YAZDIRMA (PDF) MOTORU ---
  const handlePrint = () => {
    const content = document.getElementById('career-report-print-area').innerHTML;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '1024px';
    iframe.style.height = '100%';
    iframe.style.zIndex = '-1';
    iframe.style.opacity = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    
    let stylesHTML = '';
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach(el => {
        stylesHTML += el.outerHTML;
    });

    doc.open();
    doc.write(`
        <html>
        <head>
            <title>Tercih ve Kariyer Yönelim Raporu</title>
            ${stylesHTML}
            <style>
                @media print { 
                    @page { size: A4 portrait; margin: 15mm; } 
                    body { font-family: 'Segoe UI', system-ui, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white; color: black; } 
                    .no-print { display: none !important; }
                    .page-break { page-break-before: always !important; }
                    .avoid-break { page-break-inside: avoid !important; break-inside: avoid !important; display: block; }
                    table { page-break-inside: auto; }
                    tr { page-break-inside: avoid; page-break-after: auto; }
                    thead { display: table-header-group; }
                }
            </style>
        </head>
        <body class="bg-white text-black">
            ${content}
        </body>
        </html>
    `);
    doc.close();
    
    // ZIRH: Memory Leak ve XSS'e karşı güvenli yazdırma bloğu
    setTimeout(() => { 
        if (iframe.contentWindow) {
            iframe.contentWindow.focus(); 
            
            // 🛡️ SİHİRLİ DOKUNUŞ 2: Yazdırma diyaloğu kapanınca (afterprint) anında sil
            iframe.contentWindow.onafterprint = () => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            };
            
            iframe.contentWindow.print(); 
            
            // iOS Safari gibi 'afterprint' desteklemeyen tarayıcılar için güvenli uzun fallback
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe); 
            }, 60000); // 1 saniye değil, 1 dakika sonra kesin temizlik
        }
    }, 500);
  };

  // SİHİRLİ DOKUNUŞ: Üyelik tipini kontrol eden güvenlik kalkanı (Tüm hook'lar okunduktan sonra devreye girer)
  const isPremiumUser = user && (user?.isPremium === true || user?.plan === 'premium' || user?.role === 'admin' || user?.plan === 'pro');

 

  // EĞER KULLANICI PREMIUM İSE AŞAĞIDAKİ ANA EKRAN YÜKLENİR
  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      
      {/* YENİ: UZMAN ÖĞRENCİ SEÇİM İSTASYONU */}
      <div className="bg-white p-6 rounded-3xl border border-blue-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-slate-800 flex items-center">
             Kariyer İstasyonu
          </h2>
          <p className="text-sm text-slate-500 mt-1 font-medium">Analiz edilecek öğrenciyi seçin veya yeni teste başlayın.</p>
        </div>
        
        <div className="w-full md:w-96">
          <select 
            onChange={(e) => handleSelectStudent(e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none cursor-pointer transition shadow-sm"
          >
            <option value="">+ Yeni Boş Şablon (Manuel Giriş)</option>
            {compassSubmissions.map(sub => (
              <option key={sub.firebaseId || sub.id} value={sub.firebaseId || sub.id}>
                {sub.studentClass} - {sub.studentNo} {sub.studentName ? `(${sub.studentName})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* YENİ: Çapraz Veri Radarı (Eğer öğrenci seçildiyse ve başka testleri varsa görünür) */}
      {selectedStudentSub && Object.keys(studentCrossData).length > 0 && (
         <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100 flex flex-wrap gap-4 text-sm">
            <span className="font-black text-indigo-900 flex items-center w-full md:w-auto">
               🧠 Çapraz Analiz Verileri:
            </span>
            {studentCrossData.holland && <span className="bg-white px-3 py-1 rounded-lg border border-indigo-100 text-indigo-700 font-bold shadow-sm">Holland: {studentCrossData.holland.split(':')[1] || studentCrossData.holland}</span>}
            {studentCrossData.cokluZeka && <span className="bg-white px-3 py-1 rounded-lg border border-indigo-100 text-indigo-700 font-bold shadow-sm">Baskın Zeka: {studentCrossData.cokluZeka}</span>}
            {studentCrossData.degerler && <span className="bg-white px-3 py-1 rounded-lg border border-indigo-100 text-indigo-700 font-bold shadow-sm">Değerler: {studentCrossData.degerler}</span>}
            {studentCrossData.meslekiEgilim && <span className="bg-white px-3 py-1 rounded-lg border border-indigo-100 text-indigo-700 font-bold shadow-sm">160'lık Eğilim: {studentCrossData.meslekiEgilim}</span>}
         </div>
      )}

      {/* ESKİ ANA BAŞLIK BURADAN İTİBAREN DEVAM EDİYOR */}
      <div className="bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-xl flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
          <h2 className="text-3xl font-black tracking-tight flex items-center">
            <Compass className="h-8 w-8 mr-3 text-blue-400"/> Tercih ve Kariyer Asistanı
          </h2>
          <p className="text-slate-400 mt-2 font-medium">YKS / LGS tercih sürecini stratejik ve bilimsel olarak yönetin.</p>
        </div>
        
        {/* YENİ: ÇALIŞMAYI KAYDET BUTONU */}
        {selectedStudentSub && (
          <button 
            onClick={handleSaveSession} 
            className="bg-blue-600 hover:bg-blue-500 px-6 py-4 rounded-xl font-black text-sm transition shadow-lg shadow-blue-900 flex items-center whitespace-nowrap"
          >
            <Save className="h-5 w-5 mr-2" /> Çalışmayı Kaydet
          </button>
        )}
      </div>

      <div className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm min-h-[500px]">
        <div className="flex border-b border-slate-200 overflow-x-auto custom-scrollbar">
          <button onClick={() => setActiveTab('compass')} className={`flex-1 py-4 px-6 font-bold text-sm whitespace-nowrap transition ${activeTab === 'compass' ? 'bg-slate-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:bg-slate-50'}`}>1. Kariyer Pusulası</button>
          <button onClick={() => setActiveTab('table')} className={`flex-1 py-4 px-6 font-bold text-sm whitespace-nowrap transition ${activeTab === 'table' ? 'bg-slate-50 text-blue-600 border-b-2 border-blue-600' : 'text-slate-400 hover:bg-slate-50'}`}>2. Tercih Taslak Masası</button>
          <button onClick={() => setActiveTab('ai')} className={`flex-1 py-4 px-6 font-bold text-sm whitespace-nowrap transition ${activeTab === 'ai' ? 'bg-purple-50 text-purple-600 border-b-2 border-purple-600' : 'text-slate-400 hover:bg-purple-50'}`}>3. AI Süpervizör</button>
          <button onClick={() => setActiveTab('report')} className={`flex-1 py-4 px-6 font-bold text-sm whitespace-nowrap transition ${activeTab === 'report' ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-600' : 'text-slate-400 hover:bg-emerald-50'}`}>4. Resmi Rapor</button>
        </div>

        <div className="p-4 md:p-8">
          
       {/* SEKME 1 */}
          {activeTab === 'compass' && (
             <div className="max-w-4xl mx-auto">
             
             {/* 1. DURUM: ÖĞRENCİ SEÇİLMEDİ VE MANUEL TEST BAŞLATILMADI */}
             {!selectedStudentSub && !startManual && !isTestFinished && (
                <div className="flex flex-col items-center justify-center py-16 text-center animate-in zoom-in-95 duration-300">
                   <div className="h-20 w-20 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mb-6 shadow-sm border border-blue-100">
                      <Compass className="h-10 w-10"/>
                   </div>
                   <h3 className="text-2xl font-black text-slate-800 mb-3">Analiz İçin Öğrenci Bekleniyor</h3>
                   <p className="text-slate-500 font-medium max-w-md mb-8">Lütfen yukarıdaki menüden Kariyer Pusulası anketini doldurmuş bir öğrenci seçin. Öğrencinin yönelim profili otomatik olarak buraya yüklenecektir.</p>
                   
                   <div className="flex items-center justify-center gap-4">
                      <button onClick={() => setStartManual(true)} className="px-6 py-3 bg-white border-2 border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl font-bold transition flex items-center shadow-sm">
                         Öğrenci Yanımda, Manuel Çözeceğiz
                      </button>
                   </div>
                </div>
             )}

             {/* 2. DURUM: MANUEL TEST EKRANI (Öğrenci yanımızdaysa açılır) */}
             {(startManual && !isTestFinished) && (
               <div className="flex flex-col items-center justify-center py-10 animate-in zoom-in-95 duration-300">
                 <div className="w-full max-w-lg mb-6 bg-blue-50 p-4 rounded-xl border border-blue-100 text-xs text-blue-800 text-justify">
                   <strong>Bilgilendirme:</strong> Bu 30 soruluk mikro keşif çalışması, öğrencinin ilk kariyer eğilimlerine dair ön görüşme verisi sunmak amacıyla tasarlanmıştır; <em>detaylı ve klinik bir kariyer danışmanlığı ölçeği yerine geçmez.</em>
                 </div>

                 <div className="mb-6 flex items-center justify-between w-full max-w-lg">
                   <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Soru {currentCardIndex + 1} / 30</span>
                   <div className="flex gap-1">
                     {Array.from({ length: 30 }).map((_, idx) => (
                       <div key={idx} className={`h-1.5 w-3 rounded-full ${idx < currentCardIndex ? 'bg-blue-600' : idx === currentCardIndex ? 'bg-blue-400 animate-pulse' : 'bg-slate-200'}`}></div>
                     ))}
                   </div>
                 </div>
                 <div className="bg-white border-2 border-slate-200 shadow-xl rounded-3xl p-10 w-full max-w-lg text-center min-h-[250px] flex flex-col justify-center transform transition-all duration-300 hover:scale-[1.02] hover:border-blue-300">
                   <p className="text-2xl font-black text-slate-800 leading-tight">"{CAREER_SCENARIOS[currentCardIndex].text}"</p>
                 </div>
                 <div className="flex justify-center gap-4 mt-8 w-full max-w-lg">
                   <button onClick={() => handleAnswer(0)} className="flex-1 bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 py-4 rounded-2xl font-bold flex flex-col items-center justify-center transition border border-rose-200 shadow-sm"><X className="h-8 w-8 mb-1"/> İstemem</button>
                   <button onClick={() => handleAnswer(1)} className="flex-1 bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-700 py-4 rounded-2xl font-bold flex flex-col items-center justify-center transition border border-amber-200 shadow-sm"><HelpCircle className="h-8 w-8 mb-1"/> Kararsızım</button>
                   <button onClick={() => handleAnswer(2)} className="flex-1 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:text-emerald-700 py-4 rounded-2xl font-bold flex flex-col items-center justify-center transition border border-emerald-200 shadow-sm"><Check className="h-8 w-8 mb-1"/> İsterim</button>
                 </div>
               </div>
             )}

             {/* 3. DURUM: SONUÇ EKRANI (Öğrenci Klasörü Arşivi ve Hızlı Özet) */}
             {isTestFinished && (
               <div className="animate-in slide-in-from-bottom-8 duration-500 py-6">
                 
              
                 {/* YENİ: KARİYER TESTLERİ ARŞİV TABLOSU */}
                 {selectedStudentSub && (
                   <div className="mb-10 bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                      <div className="p-5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                         <div>
                            <h3 className="font-black text-slate-800 text-lg flex items-center">
                               <FolderOpen className="h-5 w-5 mr-2 text-blue-600"/> Öğrenci Kariyer Dosyası
                            </h3>
                            <p className="text-xs text-slate-500 font-medium mt-1">Öğrencinin çözdüğü tüm mesleki yönelim envanterlerini buradan inceleyebilirsiniz.</p>
                         </div>
                      </div>
                      <div className="overflow-x-auto">
                         <table className="w-full text-left text-sm">
                            <thead className="bg-white text-xs font-bold text-slate-400 uppercase tracking-widest border-b border-slate-100">
                               <tr>
                                  <th className="p-4">Envanter Adı</th>
                                  <th className="p-4 text-center">Durum</th>
                                  <th className="p-4 text-right">İşlem</th>
                               </tr>
                            </thead>
                           <tbody className="divide-y divide-slate-50">
                                 {submissions
                                   .filter(s => s.studentNo === selectedStudentSub.studentNo && ['kariyer_pusulasi', 'lgs_pusulasi', 'holland_inventory', 'coklu_zeka_envanteri', 'mesleki_degerler_envanteri', 'mesleki_egilim_belirleme'].includes(s.testId))
                                   .map(test => (
                                  <tr key={test.firebaseId || test.id} className="hover:bg-slate-50 transition">
                                     <td className="p-4 font-bold text-slate-700">{test.testName}</td>
                                     <td className="p-4 text-center">
                                        <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded text-[10px] font-black uppercase">Tamamlandı</span>
                                     </td>
                                     <td className="p-4 text-right">
                                        {/* SİHİRLİ BUTON: Tıklanınca App.js'teki ReportViewer Pop-up'ını tetikler! */}
                                        <button 
                                          onClick={() => onAnalyze && onAnalyze(test)} 
                                          className="px-4 py-2 bg-slate-900 text-white hover:bg-blue-600 rounded-lg font-bold text-xs transition shadow-sm flex items-center inline-flex"
                                        >
                                           <Activity className="h-3 w-3 mr-2"/> Görüntüle
                                        </button>
                                     </td>
                                  </tr>
                               ))}
                            </tbody>
                         </table>
                      </div>
                   </div>
                 )}

               
                 
                 <div className="mt-8 text-center border-t border-slate-200 pt-8">
                    <button onClick={() => setActiveTab('table')} className="bg-slate-900 hover:bg-blue-600 text-white px-8 py-4 rounded-xl font-bold transition shadow-lg flex items-center justify-center mx-auto">
                       Tercih Taslak Masasına Geç <ArrowRight className="h-5 w-5 ml-2"/>
                    </button>
                 </div>
               </div>
             )}
             
             </div>
          )}

         {/* SEKME 2: TERCİH TASLAK MASASI (KLAVYE VE EXCEL MODU) */}
          {activeTab === 'table' && (
            <div className="max-w-6xl mx-auto animate-in fade-in duration-300 relative">
              
              {/* YENİ: SINAV TÜRÜ SEÇİCİ */}
              <div className="flex bg-slate-100 p-1.5 rounded-xl w-fit mx-auto mb-8 border border-slate-200 shadow-sm">
                 <button onClick={() => handleExamTypeSwitch('YKS')} className={`px-8 py-2.5 rounded-lg font-black text-sm transition-all ${examType === 'YKS' ? 'bg-white text-blue-700 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>YKS (Üniversite)</button>
                 <button onClick={() => handleExamTypeSwitch('LGS')} className={`px-8 py-2.5 rounded-lg font-black text-sm transition-all ${examType === 'LGS' ? 'bg-white text-blue-700 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>LGS (Lise)</button>
              </div>

              {/* LGS ÖZEL YASAL UYARI */}
              {examType === 'LGS' && (
                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl mb-8 flex items-start gap-4">
                      <div className="bg-amber-100 text-amber-600 p-2 rounded-full shrink-0"><AlertTriangle className="h-5 w-5"/></div>
                      <div>
                          <h4 className="font-bold text-amber-900 text-xs uppercase tracking-widest mb-1">Yerel Yerleştirme Zorunluluğu</h4>
                          <p className="text-xs text-amber-800 font-medium leading-relaxed">
                              Merkezî sınavla öğrenci alan okullar tercih ekranının açılabilmesi için öğrencilerin ilk olarak yerel yerleştirme (OBP/Adres) tercihi yapmaları zorunludur. Aksi halde merkezi yerleştirme tercih ekranı sistemsel olarak açılmayacaktır.
                          </p>
                      </div>
                  </div>
              )}

              {/* ÇOKLU PUAN/YÜZDELİK GİRİŞ ALANI */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100 mb-8">
                <div className="mb-4">
                  <h3 className="text-lg font-black text-blue-900">
                    Öğrencinin Başarı {examType === 'YKS' ? 'Sıralamaları' : 'Durumu (Yüzdelik Dilim ve OBP)'}
                  </h3>
                  <p className="text-sm font-medium text-blue-700 mt-1">
                    {examType === 'YKS' 
                      ? 'Öğrencinin girdiği puan türlerindeki başarı sıralamalarını doldurun.' 
                      : 'Öğrencinin genel/il yüzdelik dilimlerini (Örn: 4.25) ve Okul Başarı Puanını (Örn: 85.50) doldurun.'}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {(examType === 'YKS' ? SCORE_TYPES : LGS_SCORE_TYPES).map(type => (
                    <div key={type} className="bg-white p-2 rounded-xl border border-blue-200 shadow-sm flex flex-col">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-2 mb-1">{type}</label>
                      <input 
                        type="number" 
                        step="0.01" 
                        placeholder={examType === 'YKS' ? "Sıra" : (type === 'OBP' ? "Puan" : "% Dilim")} 
                        value={studentRanks[type]} 
                        onChange={(e) => handleRankChange(type, e.target.value)} 
                        className="w-full bg-transparent text-sm font-black text-slate-800 outline-none px-2" 
                      />
                    </div>
                  ))}
                </div>
              </div>

             {/* YENİ: DİNAMİK TABLO OLUŞTURUCU MOTOR */}
              {(() => {
                const renderPrefTable = (title, startIndex, endIndex, maxCount, showSmartPaste = false) => {
                  const slicedPrefs = preferences.slice(startIndex, endIndex);
                  return (
                    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm mb-8">
                      <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
                        <div>
                          <h3 className="font-bold text-slate-800 text-sm">{title} ({maxCount} Hak)</h3>
                          <p className="text-[11px] text-slate-500 font-medium">Klavyenizdeki <b>TAB</b> tuşu ile hücreler arasında hızlıca gezinebilirsiniz.</p>
                        </div>
                        {showSmartPaste && (
                          <button onClick={() => setShowPasteModal(true)} className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 px-4 py-2 rounded-xl font-bold text-xs flex items-center transition">
                            <Plus className="h-4 w-4 mr-2"/> Akıllı Metin Yapıştır
                          </button>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                          <thead className="bg-slate-100 text-slate-600 font-bold text-[10px] uppercase tracking-widest border-b border-slate-200">
                            <tr>
                              <th className="p-3 w-12 text-center border-r border-slate-200">No</th>
                              <th className="p-3 border-r border-slate-200">{examType === 'YKS' ? 'Üniversite Adı' : 'Okul Adı'}</th>
                           <th className="p-3 border-r border-slate-200">{examType === 'YKS' ? 'Bölüm / Program' : 'Alan / Lise Türü'}</th>
                              <th className="p-3 w-24 border-r border-slate-200">Tür</th>
                              <th className="p-3 w-32 border-r border-slate-200">
                                {examType === 'YKS' ? 'Geçen Yıl Sırası' : (title.includes('Yerel') ? 'Geçen Yıl OBP' : 'Yüzdelik Dilimi')}
                              </th>
                              <th className="p-3 w-40 text-center border-r border-slate-200">Risk Radarı</th>
                              <th className="p-3 w-16 text-center">Sil</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {slicedPrefs.map((pref, localIndex) => {
                              const index = startIndex + localIndex;
                              const badge = pref.rank ? getRankBadge(pref.rank, pref.scoreType) : null;
                              const BadgeIcon = badge?.icon;
                              return (
                                <tr key={pref.id} className="hover:bg-slate-50 transition-colors group">
                                  <td className="p-1 text-center font-black text-slate-400 border-r border-slate-100 bg-slate-50/50 group/number">
                                    <div className="flex flex-col items-center justify-center">
                                      <button onClick={() => movePref(index, 'up')} className="text-slate-200 hover:text-blue-600 transition opacity-0 group-hover/number:opacity-100 p-0.5"><ArrowUp className="h-3 w-3"/></button>
                                      <span className="py-0.5 text-xs">{localIndex + 1}</span>
                                      <button onClick={() => movePref(index, 'down')} className="text-slate-200 hover:text-blue-600 transition opacity-0 group-hover/number:opacity-100 p-0.5"><ArrowDown className="h-3 w-3"/></button>
                                    </div>
                                  </td>
                                  <td className="p-1 border-r border-slate-100">
                                    <input tabIndex={index * 4 + 1} type="text" value={pref.university} onChange={(e) => handlePrefChange(index, 'university', e.target.value)} placeholder={examType === 'YKS' ? 'Üniversite...' : 'Okul Adı...'} className="w-full p-2 bg-transparent outline-none font-bold text-slate-800 placeholder-slate-300 focus:bg-blue-50 focus:rounded" />
                                  </td>
                                  <td className="p-1 border-r border-slate-100">
                                    {examType === 'YKS' ? (
                                      <input tabIndex={index * 4 + 2} type="text" value={pref.department} onChange={(e) => handlePrefChange(index, 'department', e.target.value)} placeholder="Bölüm..." className="w-full p-2 bg-transparent outline-none font-medium text-slate-600 placeholder-slate-300 focus:bg-blue-50 focus:rounded" />
                                    ) : (
                                      <select tabIndex={index * 4 + 2} value={pref.department} onChange={(e) => handlePrefChange(index, 'department', e.target.value)} className="w-full p-2 bg-transparent outline-none font-medium text-slate-600 cursor-pointer focus:bg-blue-50 focus:rounded">
                                        <option value="" disabled>Tür Seçin...</option>
                                        {Object.keys(LGS_GROUPS).map(lise => (
                                          <option key={lise} value={lise}>{lise}</option>
                                        ))}
                                      </select>
                                    )}
                                  </td>
                                  <td className="p-1 border-r border-slate-100">
                                    <select tabIndex={index * 4 + 3} value={pref.scoreType} onChange={(e) => handlePrefChange(index, 'scoreType', e.target.value)} className="w-full p-2 bg-transparent outline-none font-black text-slate-700 cursor-pointer focus:bg-blue-50 focus:rounded">
                                      {(examType === 'YKS' ? SCORE_TYPES : LGS_SCORE_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                  </td>
                                  <td className="p-1 border-r border-slate-100">
                                    <input tabIndex={index * 4 + 4} type="number" step="0.01" value={pref.rank} onChange={(e) => handlePrefChange(index, 'rank', e.target.value)} placeholder={examType === 'YKS' ? 'Sıra' : (title.includes('Yerel') ? 'OBP Puanı' : '% Dilim')} className="w-full p-2 bg-transparent outline-none font-bold text-slate-800 placeholder-slate-300 focus:bg-blue-50 focus:rounded" />
                                  </td>
                                  <td className="p-2 text-center border-r border-slate-100">
                                    {badge ? (
                                      <div className={`flex items-center justify-center px-2 py-1 rounded text-[10px] font-bold ${badge.color}`}>
                                        <BadgeIcon className="h-3 w-3 mr-1"/> {badge.label}
                                      </div>
                                    ) : <span className="text-[10px] text-slate-300">-</span>}
                                  </td>
                                  <td className="p-2 text-center">
                                    <button onClick={() => deletePref(index)} className="p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded transition opacity-0 group-hover:opacity-100"><Trash2 className="h-4 w-4"/></button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                };

                // YKS ve LGS'ye göre Hangi Tablolar Çizilecek?
                if (examType === 'YKS') {
                  return renderPrefTable('Hızlı Tercih Masası', 0, 24, 24, true);
                } else {
                  return (
                    <>
                      {renderPrefTable('Yerel Yerleştirme (OBP/Adres)', 0, 5, 5, false)}
                      {renderPrefTable('Merkezî Yerleştirme (Sınav Puanı)', 5, 15, 10, false)}
                      
                      {!showBoarding ? (
                        <button onClick={() => setShowBoarding(true)} className="w-full py-5 mb-8 border-2 border-dashed border-indigo-200 text-indigo-600 bg-indigo-50/30 rounded-2xl font-bold hover:bg-indigo-50 transition flex items-center justify-center shadow-sm">
                          <Plus className="h-5 w-5 mr-2" /> Pansiyonlu Okullara Tercih Yapmak İstiyorsanız Tıklayın
                        </button>
                      ) : (
                        <div className="relative mt-8">
                          <button onClick={() => setShowBoarding(false)} className="absolute -top-3 -right-3 bg-rose-100 text-rose-600 p-2 rounded-full z-10 hover:bg-rose-200 transition shadow-md"><X className="h-4 w-4"/></button>
                          {renderPrefTable('Pansiyonlu Okullar', 15, 20, 5, false)}
                        </div>
                      )}
                    </>
                  );
               }
              })()}


              {/* AI BUTONU */}
              {getActivePrefs().length > 0 && (
                <div className="mt-8 text-center pb-10">
                  <button onClick={handleGenerateAI} disabled={aiLoading} className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-bold transition shadow-lg shadow-purple-200 flex items-center justify-center mx-auto">
                    {aiLoading ? <Loader2 className="h-5 w-5 mr-2 animate-spin"/> : <Sparkles className="h-5 w-5 mr-2"/>}
                    {aiLoading ? "Yapay Zeka İnceliyor..." : "Yapay Zeka ile Tercihleri Analiz Et"}
                  </button>
                </div>
              )}

              {/* AKILLI YAPIŞTIRMA MODALI */}
              {showPasteModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                  <div className="bg-white rounded-3xl p-6 w-full max-w-lg shadow-2xl animate-in zoom-in-95">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-black text-slate-800">Akıllı Metin Ekleme</h3>
                      <button onClick={() => setShowPasteModal(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5"/></button>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">Öğrencinin gönderdiği tercih listesini buraya yapıştırın. Sistem üniversite, bölüm, tür ve sıralamaları bulup tabloya dizecektir. <i>(Her satıra 1 tercih gelecek şekilde alt alta)</i></p>
                    <textarea 
                      value={pasteText} 
                      onChange={(e) => setPasteText(e.target.value)}
                      placeholder="Örn: Hacettepe Üniversitesi Tıp Fakültesi SAY 3500..."
                      className="w-full h-40 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 resize-none mb-4"
                    />
                    <button onClick={handleSmartPaste} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition flex items-center justify-center">
                      <Check className="h-5 w-5 mr-2"/> Tabloya Aktar
                    </button>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* SEKME 3: AI SÜPERVİZÖR */}
          {activeTab === 'ai' && (
            <div className="max-w-4xl mx-auto animate-in fade-in duration-300">
              
              {/* EĞER HAKKI BİTTİYSE PAYWALL (ÖDEME DUVARI) GÖSTER */}
              {(!isPremiumUser && localTrialCount >= 2 && !aiResult) ? (
                <div className="bg-slate-900 p-10 rounded-[2rem] border border-slate-800 shadow-2xl text-center flex flex-col items-center justify-center min-h-[400px]">
                  <div className="h-20 w-20 bg-gradient-to-br from-amber-400 to-orange-600 text-white rounded-full flex items-center justify-center mb-6 shadow-lg shadow-orange-900/50 mx-auto">
                    <ShieldCheck className="h-10 w-10"/>
                  </div>
                  <h2 className="text-3xl font-black text-white mb-4 tracking-tight">Limitinize Ulaştınız <span className="text-amber-500">PRO</span></h2>
                  <p className="text-slate-400 font-medium max-w-lg mx-auto mb-8 leading-relaxed">
                    Ücretsiz 2 deneme hakkınızı doldurdunuz. Yapay zeka destekli stratejik tercih analizi ve çapraz envanter değerlendirmesi gibi profesyonel araçları sınırsız kullanmak için <strong className="text-white">Premium</strong> sürüme geçmelisiniz.
                  </p>
                 <button onClick={onUpgrade} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white px-8 py-4 rounded-xl font-black text-sm transition shadow-lg shadow-orange-900/20 flex items-center mx-auto">
  <Sparkles className="h-5 w-5 mr-2" /> PRO Sürüme Yükselt
</button>
                </div>
              ) : (
                /* HAKKI VARSA VEYA PREMIUM İSE NORMAL AI EKRANINI GÖSTER */
                <>
                  <div className="flex justify-between items-center mb-6 border-b border-purple-200 pb-4">
                    <h3 className="font-black text-purple-900 text-lg flex items-center uppercase tracking-wide">
                      <Brain className="h-6 w-6 mr-3 text-purple-600"/> AI Stratejik Liste Analizi
                    </h3>
                    {/* Ücretsiz kullanıcıya kalan hakkını zarifçe göster */}
                    {!isPremiumUser && (
                      <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-lg text-xs font-bold flex items-center">
                        <AlertTriangle className="h-3 w-3 mr-1"/> Kalan Deneme: {Math.max(0, 2 - localTrialCount)}
                      </span>
                    )}
                  </div>
                  
                  {!aiResult ? (
                    <div className="bg-purple-50 p-10 rounded-2xl border border-purple-100 text-center flex flex-col items-center justify-center">
                       <Sparkles className="h-16 w-16 text-purple-200 mb-4"/>
                       <p className="text-purple-800 font-bold mb-6 max-w-md">Henüz analiz yapılmadı. Lütfen 2. sekmedeki tablonuzu doldurduktan sonra analiz butonuna tıklayın.</p>
                       <button onClick={() => setActiveTab('table')} className="px-6 py-3 bg-white text-purple-600 border border-purple-200 rounded-xl font-bold text-sm shadow-sm hover:bg-purple-100 transition">Tabloya Dön</button>
                    </div>
                  ) : (
                    <div className="bg-white p-6 rounded-2xl border border-purple-200 shadow-sm">
                      <EditableAIBox text={aiResult} />
                      
                      <div className="mt-8 border-t border-slate-100 pt-6 flex justify-between items-center">
                         <button onClick={() => setActiveTab('table')} className="text-sm font-bold text-slate-500 hover:text-blue-600 transition">Düzenleme Yapmak İçin Tabloya Dön</button>
                         <button onClick={() => setActiveTab('report')} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold text-sm transition shadow-md flex items-center">
                            Son Raporu Hazırla <ArrowRight className="h-4 w-4 ml-2"/>
                         </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* SEKME 4: RESMİ RAPOR (PDF ÇIKTISI) */}
          {activeTab === 'report' && (
            <div className="max-w-4xl mx-auto animate-in fade-in duration-300">
              
              {/* ÜST BUTON BAR (Yazdırıldığında Gizlenir) */}
              <div className="flex justify-between items-center mb-6 border-b border-emerald-200 pb-4 no-print">
                <h3 className="font-black text-emerald-900 text-lg flex items-center uppercase tracking-wide">
                  <Printer className="h-6 w-6 mr-3 text-emerald-600"/> Resmi Çıktı Önizlemesi
                </h3>
                <button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition shadow-md flex items-center">
                  <Printer className="h-4 w-4 mr-2"/> Raporu Yazdır / PDF Al
                </button>
              </div>

              {/* === YAZDIRILACAK A4 ALANI === */}
              <div id="career-report-print-area" className="bg-white p-8 md:p-12 rounded-2xl border border-slate-200 shadow-sm print:border-none print:shadow-none print:p-0">
                
                {/* 1. Kurum Başlığı */}
                <div className="text-center border-b-2 border-slate-800 pb-6 mb-8">
                  <h1 className="text-2xl font-black text-slate-900 uppercase tracking-widest mb-1">Psikolojik Danışmanlık ve Rehberlik Servisi</h1>
                  <h2 className="text-xl font-bold text-slate-700">{examType} Tercih ve Kariyer Yönelim Raporu</h2>
                </div>

               {/* 2. Öğrenci Sınav Bilgileri */}
                <div className="mb-8 avoid-break">
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4 flex items-center">
                    <Target className="h-5 w-5 mr-2 text-slate-500"/> Sınav ve Başarı {examType === 'YKS' ? 'Sıralaması' : 'Durumu'} Bilgileri
                  </h3>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {(examType === 'YKS' ? SCORE_TYPES : LGS_SCORE_TYPES).map(type => (
                      <div key={type} className="bg-slate-50 p-3 rounded-xl border border-slate-200 text-center">
                        <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                          {examType === 'YKS' ? `${type} SIRASI` : (type === 'OBP' ? 'OBP PUANI' : `${type} DİLİM`)}
                        </span>
                        <span className="block font-black text-slate-800">
                          {studentRanks[type] 
                            ? (examType === 'YKS' ? parseInt(studentRanks[type]).toLocaleString('tr-TR') : studentRanks[type]) 
                            : '-'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 3. Kariyer ve İlgi Profili */}
                <div className="mb-8 avoid-break">
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4 flex items-center">
                    <Activity className="h-5 w-5 mr-2 text-slate-500"/> Kariyer ve Psikolojik Yönelim Profili
                  </h3>
                  {isTestFinished ? (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                        {topGroups.map((group, index) => (
                          <div key={index} className="p-4 rounded-xl border border-slate-200 bg-slate-50">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block mb-1">{index + 1}. TEMEL YÖNELİM</span>
                            <span className="font-black text-slate-800 text-sm block mb-1">{group.name}</span>
                            <span className="text-xs font-medium text-slate-500">{group.desc}</span>
                          </div>
                        ))}
                      </div>
                      
                      {/* YENİ: Boşluğu Dolduran Dinamik Uzman Değerlendirme Paragrafı */}
                      <div className="p-5 rounded-xl border border-blue-100 bg-blue-50/50 text-sm text-slate-700 leading-relaxed text-justify">
                        <strong className="text-blue-900 font-black">Uzman Ön Değerlendirmesi: </strong> 
                        Öğrencinin mikrotest analizine göre öncelikli olarak <strong>{topGroups[0].name}</strong> alanına belirgin bir ön eğilim göstermektedir. Bu durum, öğrencinin <em>{topGroups[0].desc}</em> gibi disiplinlerde hem akademik olarak başarılı hem de mesleki olarak tatminkâr olma potansiyelinin yüksek olduğunu gösterir. Ayrıca, <strong>{topGroups[1].name}</strong> ve <strong>{topGroups[2].name}</strong> alanları da öğrencinin çok yönlü kariyer gelişimini destekleyen ikincil motivasyon kaynaklarıdır. Tercih listesi oluşturulurken, öğrencinin potansiyelini yansıtan bu üç temel psikolojik yönelimini merkeze alan programlara öncelik verilmesi tavsiye edilmektedir.
                      </div>
                    </>
                  ) : (
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm font-medium text-slate-500 text-center">
                      Öğrenci Kariyer Pusulası testini henüz çözmediği için yönelim profili oluşturulamadı.
                    </div>
                  )}
                </div>

                {/* 4. Tercih Listesi ve Risk Tablosu */}
                <div className="mb-8 avoid-break">
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4 flex items-center">
                    <List className="h-5 w-5 mr-2 text-slate-500"/> Taslak Tercih Listesi ve Stratejik Analiz
                  </h3>

                  {(() => {
                    // YENİ: PDF için Özel Tablo Çizici
                    const renderPrintTable = (title, startIndex, endIndex, isOBP = false) => {
                      const tablePrefs = preferences.slice(startIndex, endIndex).filter(p => p.university && p.department && p.rank);
                      if (tablePrefs.length === 0) return null; // Sadece içi dolu olan tabloları PDF'e bas

                      return (
                        <div className="mb-6">
                          <h4 className="text-xs font-black text-slate-700 uppercase bg-slate-100 p-2.5 border border-slate-200 border-b-0 rounded-t-lg">{title}</h4>
                          <table className="w-full text-left border-collapse border border-slate-200 text-sm">
                            <thead className="bg-slate-50 text-slate-600 font-bold text-[10px] print:text-[9px] uppercase">
                              <tr>
                                <th className="p-2 print:p-1 border border-slate-200 w-10 text-center">No</th>
                                <th className="p-2 print:p-1 border border-slate-200">{examType === 'YKS' ? 'Üniversite / Bölüm' : 'Okul / Alan'}</th>
                                <th className="p-2 print:p-1 border border-slate-200 text-center w-16">Tür</th>
                                <th className="p-2 print:p-1 border border-slate-200 text-center w-24">{examType === 'YKS' ? 'Sırası' : (isOBP ? 'OBP Puanı' : 'Yüzdeliği')}</th>
                                <th className="p-2 print:p-1 border border-slate-200 text-center w-32">Risk Durumu</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tablePrefs.map((pref, localIndex) => {
                                const badge = getRankBadge(pref.rank, pref.scoreType);
                                const displayRank = examType === 'YKS' ? parseInt(pref.rank).toLocaleString('tr-TR') : pref.rank;
                                
                                return (
                                  <tr key={pref.id} className="border-b border-slate-200 print:text-[10px]">
                                    <td className="p-2 print:p-1 border border-slate-200 text-center font-bold text-slate-500">{localIndex + 1}</td>
                                    <td className="p-2 print:p-1 border border-slate-200">
                                      <span className="font-bold text-slate-800 block">{pref.university}</span>
                                      <span className="text-xs print:text-[9px] text-slate-600 block">{pref.department}</span>
                                    </td>
                                    <td className="p-2 print:p-1 border border-slate-200 text-center font-black text-slate-700">{pref.scoreType}</td>
                                    <td className="p-2 print:p-1 border border-slate-200 text-center font-bold text-slate-800">{displayRank}</td>
                                    <td className="p-2 print:p-1 border border-slate-200 text-center">
                                      <span className={`text-[10px] print:text-[8px] px-2 py-1 font-bold uppercase rounded ${badge?.color || 'text-slate-500'}`}>
                                        {badge ? badge.label : '-'}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    };

                    const activePrefs = getActivePrefs();
                    if (activePrefs.length === 0) {
                      return (
                        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-sm font-medium text-slate-500 text-center">
                          Tercih tablosu henüz doldurulmadı.
                        </div>
                      );
                    }

                    // YKS ve LGS için hangi tabloların PDF'e gideceğine karar ver
                    if (examType === 'YKS') {
                      return renderPrintTable('YKS Taslak Tercih Listesi', 0, 24);
                    } else {
                      return (
                        <>
                          {renderPrintTable('Yerel Yerleştirme (OBP/Adres)', 0, 5, true)}
                          {renderPrintTable('Merkezî Yerleştirme (Sınav Puanı)', 5, 15)}
                          {showBoarding && renderPrintTable('Pansiyonlu Okullar', 15, 20)}
                        </>
                      );
                    }
                  })()}
                </div>

                {/* 5. Uzman Değerlendirmesi (AI veya Manuel) */}
                <div className="mb-12 print:mt-6">
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest border-b border-slate-200 pb-2 mb-4 flex items-center">
                    <Brain className="h-5 w-5 mr-2 text-slate-500"/> Psikolojik Danışman Değerlendirmesi
                  </h3>
                  {aiResult ? (
                    <div 
                      className="text-sm text-slate-800 leading-7 text-justify" 
                      dangerouslySetInnerHTML={formatAIText(aiResult)} 
                    />
                  ) : (
                    <div className="p-6 bg-slate-50 rounded-xl border border-slate-200 min-h-[150px] flex items-center justify-center text-slate-400 text-sm italic">
                      [ Uzman Notları ve Değerlendirmeleri İçin Ayrılmış Alan ]
                    </div>
                  )}
                </div>

{/* YENİ: Uzman Kontrol Listesi ve Sınırlılıklar */}
                <div className="mb-8 avoid-break border-t-2 border-slate-800 pt-6 mt-12">
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-4 flex items-center">
                    <Check className="h-5 w-5 mr-2 text-emerald-600"/> Uzman Tercih Kontrol Listesi
                  </h3>
                  <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-xs text-slate-700 font-medium mb-6">
  {examType === 'YKS' ? (
    <>
      <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Özel koşullar kontrol edildi mi?</div>
      <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Burs/ücret bilgisi değerlendirildi mi?</div>
      <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Şehir/konaklama şartları konuşuldu mu?</div>
    </>
  ) : (
    <>
      <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Yerel yerleştirme şartları sağlandı mı?</div>
      <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Yüzdelik dilim uyumu kontrol edildi mi?</div>
      <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Pansiyon/Ulaşım durumu konuşuldu mu?</div>
    </>
  )}
  <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Öğrenci ilgi alanı ile uyum değerlendirildi mi?</div>
  <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Tercih listesinde güvenli liman var mı?</div>
  <div className="flex items-center"><div className="w-4 h-4 border border-slate-400 rounded mr-3"></div> Veli/Aile onayı alındı mı?</div>
</div>
                  
                  <div className="bg-slate-50 p-4 rounded-lg text-[10px] text-slate-500 text-justify italic">
                    <strong>Etik ve Yasal Sınırlılık Notu:</strong> Bu rapor, öğrencinin psikolojik eğilimleri ile geçen yıla ait sıralama verileri baz alınarak hazırlanan stratejik bir "ön taslaktır". Analiz yalnızca girilen başarı sırası üzerinden yapılmaktadır; yıllara göre değişen kontenjan, güncel kılavuz kuralları, üniversitelerin özel/vakıf ücretleri veya ek koşulları sistem tarafından garanti edilemez. Nihai tercih kararı ve güncel kılavuz kontrolü öğrenci ve velinin sorumluluğundadır.
                  </div>
                </div>

                {/* 6. İmza Sirküleri */}
                <div className="mt-16 pt-8 avoid-break w-full">
                  <table className="w-full table-fixed text-center border-collapse">
                    <tbody>
                      <tr>
                        <td className="w-1/3 align-bottom pb-2">
                          <span className="block text-sm font-black text-slate-800 uppercase mb-8">Öğrenci</span>
                          <div className="border-b-2 border-slate-800 w-32 mx-auto"></div>
                          <span className="block text-[10px] text-slate-500 mt-2">İmza</span>
                        </td>
                        <td className="w-1/3 align-bottom pb-2">
                          <span className="block text-sm font-black text-slate-800 uppercase mb-8">Veli</span>
                          <div className="border-b-2 border-slate-800 w-32 mx-auto"></div>
                          <span className="block text-[10px] text-slate-500 mt-2">İmza</span>
                        </td>
                        <td className="w-1/3 align-bottom pb-2">
                          <span className="block text-sm font-black text-slate-800 uppercase mb-8">Psikolojik Danışman</span>
                          <div className="border-b-2 border-slate-800 w-40 mx-auto"></div>
                          <span className="block text-[10px] text-slate-500 mt-2">Kaşe / İmza</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>
            </div>
          )}

        </div>
      </div>
{/* 🌟 ŞIK REACT MODAL BURAYA GELECEK 🌟 */}
      {uiModal.isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-200">
            
            <div className="flex flex-col items-center text-center mb-6">
              <div className={`h-16 w-16 rounded-full flex items-center justify-center mb-4 shadow-inner ${uiModal.type === 'alert' ? 'bg-rose-100 text-rose-600 border border-rose-200' : 'bg-blue-100 text-blue-600 border border-blue-200'}`}>
                {uiModal.type === 'alert' ? <AlertTriangle className="h-8 w-8"/> : <HelpCircle className="h-8 w-8"/>}
              </div>
              <h3 className="text-xl font-black text-slate-800 tracking-tight">{uiModal.title}</h3>
              <p className="text-sm font-medium text-slate-500 mt-2 whitespace-pre-wrap">{uiModal.message}</p>
            </div>

            <div className="flex gap-3 w-full">
              {uiModal.type === 'confirm' && (
                <button onClick={() => setUiModal({ isOpen: false })} className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-sm transition">
                  İptal
                </button>
              )}
              <button 
                onClick={() => {
                  if (uiModal.type === 'confirm' && uiModal.onConfirm) {
                    uiModal.onConfirm();
                  } else {
                    setUiModal({ isOpen: false });
                  }
                }} 
                className={`flex-1 py-3.5 text-white rounded-xl font-bold text-sm transition shadow-lg ${uiModal.type === 'alert' ? 'bg-slate-900 hover:bg-slate-800' : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'}`}
              >
                {uiModal.type === 'confirm' ? 'Evet, Başlat' : 'Anladım'}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default CareerCompass;